import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, formatApiError } from "@/lib/api";
import { PageHeader } from "./AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";

const empty = { name: "", duration_label: "", duration_days: 30, price: 0, original_price: 0, currency: "USD", features: "", popular: false, enabled: true, order: 0 };

export default function AdminPackages() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const { data: packages = [] } = useQuery({ queryKey: ["admin-packages"], queryFn: async () => (await api.get("/packages?include_disabled=true")).data });

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, features: (p.features || []).join("\n") });
    setDialog(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, duration_days: Number(form.duration_days), price: Number(form.price), original_price: Number(form.original_price) || null, order: Number(form.order), features: form.features.split("\n").map((f) => f.trim()).filter(Boolean) };
      if (editing) return (await api.put(`/admin/packages/${editing.id}`, payload)).data;
      return (await api.post("/admin/packages", payload)).data;
    },
    onSuccess: () => { toast.success(editing ? "Package updated" : "Package created"); setDialog(false); qc.invalidateQueries({ queryKey: ["admin-packages"] }); qc.invalidateQueries({ queryKey: ["packages"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const toggle = useMutation({
    mutationFn: async (p) => api.put(`/admin/packages/${p.id}`, { enabled: !p.enabled }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-packages"] }); qc.invalidateQueries({ queryKey: ["packages"] }); },
  });

  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/packages/${id}`),
    onSuccess: () => { toast.success("Package deleted"); qc.invalidateQueries({ queryKey: ["admin-packages"] }); qc.invalidateQueries({ queryKey: ["packages"] }); },
  });

  return (
    <div>
      <PageHeader title="Packages" subtitle="Manage your IPTV subscription plans"
        action={<Button data-testid="add-package-btn" onClick={openNew} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0"><Plus className="w-4 h-4 mr-1" /> Add Package</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {packages.map((p) => (
          <div key={p.id} data-testid={`admin-package-${p.id}`} className={`glass rounded-2xl p-5 ${!p.enabled ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold text-white">{p.name}</h3>
                  {p.popular && <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />}
                </div>
                <p className="text-xs text-purple-400 uppercase tracking-wide">{p.duration_label} · {p.duration_days}d</p>
              </div>
              <div className="text-right">
                <div className="font-heading text-xl font-extrabold text-white">{money(p.price, p.currency)}</div>
                {p.original_price > p.price && <div className="text-xs text-slate-500 line-through">{money(p.original_price, p.currency)}</div>}
              </div>
            </div>
            <ul className="text-xs text-slate-400 space-y-1 my-3 max-h-24 overflow-hidden">
              {p.features?.slice(0, 4).map((f, i) => <li key={i}>• {f}</li>)}
            </ul>
            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Switch data-testid={`toggle-package-${p.id}`} checked={p.enabled} onCheckedChange={() => toggle.mutate(p)} />
                <span className="text-xs text-slate-400">{p.enabled ? "Enabled" : "Disabled"}</span>
              </div>
              <div className="flex gap-1">
                <Button data-testid={`edit-package-${p.id}`} size="icon" variant="ghost" onClick={() => openEdit(p)} className="text-slate-300 hover:text-white h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                <Button data-testid={`delete-package-${p.id}`} size="icon" variant="ghost" onClick={() => { if (confirm(`Delete ${p.name}?`)) remove.mutate(p.id); }} className="text-red-400 hover:text-red-300 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#0b0c12] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Package" : "New Package"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Name</Label><Input data-testid="pkg-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Duration Label</Label><Input data-testid="pkg-form-label" value={form.duration_label} onChange={(e) => setForm({ ...form, duration_label: e.target.value })} placeholder="Monthly" className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-slate-300">Days</Label><Input data-testid="pkg-form-days" type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Price</Label><Input data-testid="pkg-form-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Was</Label><Input data-testid="pkg-form-original" type="number" step="0.01" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Sort Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
            <div><Label className="text-slate-300">Features (one per line)</Label><Textarea data-testid="pkg-form-features" rows={5} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="pkg-form-popular" checked={form.popular} onCheckedChange={(v) => setForm({ ...form, popular: v })} /> Popular</label>
              <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="pkg-form-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /> Enabled</label>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="pkg-form-save" onClick={() => save.mutate()} disabled={save.isPending} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">Save Package</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
